import type {
  DirectoryContactDto,
  DirectoryContactGroupDto,
} from "@/services/api";
import type { ChatUser } from "@/features/messages/data/types";
import type { AuthProfile } from "@/types/api";

export type ContactSource = "internal" | "external";

export type ContactRecord = {
  id: string;
  name: string;
  phone: string;
  email?: string;
  role: string;
  department: string;
  initials: string;
  favourite: boolean;
  online?: boolean;
  groupIds: string[];
  description?: string;
  createdBy?: string;
  createdById?: number | null;
  createdAt?: string;
  /** Team user vs directory/phone-book contact. */
  source: ContactSource;
  /** SIP/extension when this is an internal teammate. */
  extension?: string;
  /** Logged-in user shown in Internal as "(me)". */
  isMe?: boolean;
};

export type ContactGroup = {
  id: string;
  name: string;
  description: string;
  initials: string;
  memberIds: string[];
  contactCount: number;
};

function initialsFromName(name: string) {
  const parts = name.trim().split(/\s+/).filter(Boolean);
  if (parts.length >= 2) {
    return `${parts[0]![0] ?? ""}${parts[1]![0] ?? ""}`.toUpperCase();
  }
  return name.trim().slice(0, 2).toUpperCase() || "?";
}

export function mapDirectoryContact(dto: DirectoryContactDto): ContactRecord {
  const groupId = dto.group_id != null ? String(dto.group_id) : "";
  return {
    id: `ext-${dto.id}`,
    name: dto.name,
    phone: dto.phone || "",
    email: dto.email || undefined,
    role: dto.description?.trim() || "External",
    department: dto.group_name || "Ungrouped",
    initials: initialsFromName(dto.name),
    favourite: false,
    online: false,
    groupIds: groupId ? [groupId] : [],
    description: dto.description || undefined,
    createdBy: dto.created_by_name || dto.created_by_email || undefined,
    createdById: dto.created_by_id ?? null,
    createdAt: dto.created,
    source: "external",
  };
}

export function mapInternalContact(user: ChatUser, options?: { isMe?: boolean }): ContactRecord {
  const extension = (user.extension || user.username || "").trim();
  const contactNumber = (user.phone || "").trim();
  // Prefer extension for dialing; fall back to contact number.
  const phone = extension || contactNumber;
  return {
    id: `int-${user.id}`,
    name: user.name || user.username,
    phone,
    email: user.email || undefined,
    role: options?.isMe ? "You" : "Team",
    department: "Internal",
    initials: user.initials || initialsFromName(user.name || user.username),
    favourite: false,
    online: Boolean(user.online || user.is_online),
    groupIds: [],
    extension: extension || undefined,
    source: "internal",
    isMe: options?.isMe,
  };
}

export function mapSelfContact(profile: AuthProfile): ContactRecord {
  const sip =
    profile.webphone && "sip_username" in profile.webphone
      ? String(profile.webphone.sip_username || "").trim()
      : "";
  const extension = sip || (profile.username || "").trim();
  const contactNumber = (profile.ph_no || "").trim();
  const phone = extension || contactNumber;
  return {
    id: `int-${profile.id}`,
    name: profile.name || profile.username,
    phone,
    email: profile.email || undefined,
    role: "You",
    department: "Internal",
    initials: initialsFromName(profile.name || profile.username),
    favourite: false,
    online: true,
    groupIds: [],
    extension: extension || undefined,
    source: "internal",
    isMe: true,
  };
}

export function mapDirectoryContactGroup(
  dto: DirectoryContactGroupDto,
  memberIds: string[] = [],
): ContactGroup {
  return {
    id: String(dto.id),
    name: dto.name,
    description: dto.description || "",
    initials: initialsFromName(dto.name),
    memberIds,
    contactCount: dto.contact_count ?? memberIds.length,
  };
}
